import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  closestCenter,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
  type DropAnimation,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { cn } from '~/lib/utils';
import { DirectoryAddMenu } from './DirectoryAddMenu';
import { DirectoryHeader } from './DirectoryHeader';
import { DirectoryRow, SkeletonRow } from './DirectoryRow';
import { ArtifactCard } from './ArtifactCard';
import { ImageCard } from './ImageCard';
import { KindMenu, VIEW_LABEL } from './KindMenu';
import { OtherSourcesEmpty } from './OtherSourcesEmpty';
import { RESULT_FEEDBACK, RowMenu, rowMenuHeight } from './RowMenu';
import { SortableDirectoryItem } from './SortableDirectoryItem';
import { useDirectoryReorder } from './useDirectoryReorder';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './DirectoryPanel.module.css';
import type {
  ArtifactLayout,
  DirectoryAddItem,
  DirectoryItem,
  DirectoryKind,
  DirectoryMenu,
  DirectoryRowAction,
  DirectoryView,
  HeaderTheme,
} from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';
import { useIsomorphicLayoutEffect } from '~/lib/useIsomorphicLayoutEffect';


/** One labelled group of a sectioned view: some items under a heading. */
export interface DirectorySection {
  /**
   * This section's identity in the list.
   *
   * Optional, because the `all` view has exactly one section per kind and the kind IS the identity
   * there. `other` needs it: every one of its sections is the same `kind` (see `otherSources.ts`),
   * so keying on `kind` would give Figma and Linear the same React key — React would reuse one
   * section's DOM for the other's rows the moment a second app connects, which is the bug you find
   * a week later and cannot reproduce. Falls back to `kind`.
   */
  id?: string;
  /**
   * How the group LAYS OUT: images take the grid, artifacts the one-track grid, everything else
   * plain rows. It is not the group's identity — see `id`.
   */
  kind: DirectoryKind;
  label: string;
  items: DirectoryItem[];
}

/**
 * A place OUTSIDE the list a lifted item can be dropped on: the artifact on the stage, for the Images view
 * (Beka, 2026-09-25: "drag the images from the directory and put on the artifact"). Points are the
 * pointer's, in client px. Only a pointer drag goes out; the keyboard's lift stays a reorder.
 */
export interface DirectoryDropOut {
  /**
   * A pointer drag began. The host raises whatever has to stand over its target now: an iframe under the
   * pointer would take the drag's pointer events for itself.
   */
  start: (id: string) => void;
  /** The pointer came up outside the panel. True when the drop was taken there. */
  drop: (id: string, point: { x: number; y: number }) => boolean;
  /** The drag is over, however it ended. */
  end: () => void;
}

/** The pointer's place during a pointer drag: where it went down, plus how far it has travelled. */
function pointerOf(event: Pick<DragEndEvent, 'activatorEvent' | 'delta'>): { x: number; y: number } | null {
  const start = event.activatorEvent as { clientX?: unknown; clientY?: unknown } | null;
  if (!start || typeof start.clientX !== 'number' || typeof start.clientY !== 'number') return null;
  return { x: start.clientX + event.delta.x, y: start.clientY + event.delta.y };
}

/** The design's resting width. The pane is resizable, so this is a starting position, not a rule. */
export const DIRECTORY_WIDTH = 252;

/**
 * Must match `--hv15-dur-menu-out`. A closing menu stays mounted this long so the exit transition
 * has something to run on — React would otherwise pull the node out on the same frame and the menu
 * would blink out of a fade-in.
 */
const MENU_EXIT_MS = 120;

/**
 * How many rows the skeleton draws. Six is the file's number, and it is a fixed count rather than
 * "however many fit": the point of the skeleton is to say a list is coming, and filling an 900px
 * pane with placeholder rows promises a length nobody has counted yet.
 */
const SKELETON_ROWS = 6;

/** The menus keep 6px off the pane's bottom edge, the same air the list keeps off its own. */
const MENU_EDGE_INSET = 6;

/**
 * The air between the `⋯` and the menu it opens, matching `PreviewBar`'s own — the two menus are the
 * same menu off the same glyph and they now drop the same distance.
 */
const MENU_ANCHOR_GAP = 6;

/**
 * The lifted item landing.
 *
 * It does not TRAVEL, and that is the thing to know about this drop. `onReorder` re-renders the
 * list on the same tick the pointer comes up, so by the drop's first frame the real row is already
 * sitting at its new index — measured frame by frame: overlay top 210, row top 210, from frame
 * zero. dnd-kit therefore computes a zero-distance drop and the copy simply holds still.
 *
 * Which leaves exactly one job: putting the LIFT away. Left alone the copy hovers over the finished
 * row at `scale(1.02)` on its opaque plate for the whole duration and then vanishes, and the eye
 * reads that unmount as the item popping a second time after it had already landed. `sideEffects`
 * runs as the drop begins and flags the copy as landing; the stylesheet takes the scale, the shadow
 * and the plate back off it, so by the time it leaves the DOM it is identical to the row underneath
 * and its removal changes nothing on screen.
 *
 * `ease-out` at 200ms — a drop is the system answering a decision already made.
 */
/**
 * NO drop animation, on Guri's call (2026-08-30): "when I hand off the artifact it should be
 * immediately released… very very fast." The previous build flew the lifted copy home over 200ms
 * with a carefully staged landing; it was correct choreography and it still read as lag, because
 * the reorder itself already happened on the tick the pointer came up — the flight was animating a
 * decision the list had finished making. `null` unmounts the copy on release and the real row is
 * already sitting at its destination in the same frame, so nothing doubles and nothing travels.
 * (The `data-landing` styles in the row/card sheets are now unused; they stay for the day a
 * subtler settle is wanted back.)
 */
const DROP_ANIMATION: DropAnimation | null = null;

/**
 * The rows of whichever menu is open. `MenuRow` publishes `role="menuitem"`, so this is the one
 * selector that finds all three menus' rows without any of them knowing it is being walked. A
 * disabled row is not a stop — arrowing onto something that cannot be picked is a dead key.
 */
const MENU_ITEM = '[role="menuitem"]:not(:disabled)';

/** Two menus are never open at once, so identity is one string. */
function menuKey(menu: DirectoryMenu | null): string | null {
  if (menu === null) return null;
  return menu.menu === 'row' ? `row:${menu.rowId}` : menu.menu;
}

/**
 * Keeps the closing menu mounted for the length of its exit.
 *
 * Reopening mid-exit cancels it rather than queueing behind it, so a double-click on a trigger
 * lands on a menu that is already coming back rather than one that has to finish leaving first.
 */
function useMenuPresence(menu: DirectoryMenu | null) {
  const [rendered, setRendered] = useState<DirectoryMenu | null>(menu);
  const [closing, setClosing] = useState(false);
  const renderedRef = useRef(rendered);
  renderedRef.current = rendered;
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const key = menuKey(menu);

  useEffect(() => {
    clearTimeout(timer.current);
    if (menu) {
      setRendered(menu);
      setClosing(false);
      return;
    }
    if (renderedRef.current === null) return;
    setClosing(true);
    timer.current = setTimeout(() => {
      setRendered(null);
      setClosing(false);
    }, MENU_EXIT_MS);
    return () => clearTimeout(timer.current);
    // Keyed on the identity string, not the object: a parent that rebuilds `{ menu: 'add' }` on
    // every render would otherwise restart the entrance on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return { rendered, closing };
}

export interface DirectoryPanelProps {
  /** Controlled view. Leave undefined and the panel holds it, so a story switches standalone. */
  kind?: DirectoryView;
  defaultKind?: DirectoryView;
  onKindChange?: (view: DirectoryView) => void;
  /**
   * How artifacts draw: the rail of renders (`grid`, the default) or the list of rows. Controlled
   * the same way `kind` is — leave it undefined and the panel holds it. The band's toggle is drawn
   * only on the artifacts view, because it is the only kind with two layouts.
   */
  artifactLayout?: ArtifactLayout;
  /**
   * What plays over an artifact tile while it is being written. The panel asks per item and mounts
   * whatever comes back inside the thumbnail; the host owns the morph, the panel owns the slot.
   */
  renderWorking?: (item: DirectoryItem) => React.ReactNode;
  defaultArtifactLayout?: ArtifactLayout;
  onArtifactLayoutChange?: (layout: ArtifactLayout) => void;

  /** The items of the view on screen, in the deck's order. In-flight items included, in place. */
  items: DirectoryItem[];
  /**
   * The `all` view's grouping: the SAME items, split under kind labels. When present the list
   * renders section by section; `items` stays the flat list so selection, menus and the count keep
   * one source of truth. Never combined with sorting — a blend has no one order to write back.
   */
  sections?: DirectorySection[];
  /**
   * Overrides the count in the header. The panel counts `items` by default; pass this when the
   * list on screen is a window onto a longer one.
   */
  count?: number;

  /** The open item — the one the preview is showing. */
  selectedId?: string | null;
  onSelect?: (id: string) => void;

  /** Draws the skeleton instead of the list, and hides the count. */
  loading?: boolean;

  onAdd?: (item: DirectoryAddItem) => void;
  /**
   * Leave for the integrations screen — the only action the `other` view has.
   *
   * The panel does not navigate (it is a design-system component; the route shell owns routes), so
   * without this handler the Other sources view has no way out and its `+` falls back to the add
   * menu, which makes deck contents rather than connections. Supply it and the `+` becomes
   * "Connect an app" and the empty state grows its button.
   */
  onConnectApps?: () => void;
  /**
   * Apps this account has already connected, by display name — `otherSources.connectedAppNames`.
   *
   * Only the Other sources empty state reads it, and only to stop inviting somebody to connect
   * Figma when Figma is already connected. Absent reads as "none", which is the common case and the
   * right thing to assume while the status query is still in flight.
   */
  connectedApps?: string[];
  onRowAction?: (action: DirectoryRowAction, id: string) => void;
  /**
   * The rows an item's `⋯` menu draws, in order — the host's per-item cut of `KIND_ROW_ACTIONS`
   * (`undo-edit` only with a held previous source, `extract-styles` never on a social post). Every
   * row returned must be wired, per Header rule 5.
   *
   * Absent, the panel draws the base four-row menu and images get no `⋯` at all — exactly the
   * pre-per-kind behaviour, which is what the stories render.
   */
  menuRowsFor?: (item: DirectoryItem, kind: DirectoryKind) => DirectoryRowAction[];
  /**
   * No `⋯` on any row, at all.
   *
   * For a list whose rows OPEN something and do nothing else — the brand kit's seven sections and
   * its twenty-one composition slots are fixed, so there is nothing to duplicate, rename or delete.
   * Without this the panel falls back to its base four-row menu, and with no `onRowAction` behind it
   * every one of those rows is a control that answers nothing: Delete does nothing, and Rename opens
   * a field that throws the new name away. A dead menu is worse than no menu.
   */
  hideRowMenu?: boolean;
  /**
   * A row's name was changed — by a double-click on it, or by `Rename` in its `⋯`.
   *
   * Omit it and no row opens for editing and the menu drops its Rename row. The panel does not
   * decide which kinds have names worth changing — the caller does, by supplying this or not.
   */
  onRename?: (id: string, next: string) => void;
  /**
   * A drag finished somewhere new. Reports the whole order rather than a from/to pair, because the
   * panel is controlled on `items` and the caller has to write an array either way — handing it two
   * indices would make every caller re-derive the same `arrayMove`.
   *
   * Omit it and the list is not sortable at all: no sensors are attached and no row can be lifted.
   * A list that reorders under the pointer and forgets on the next render is worse than one that
   * does not move.
   */
  onReorder?: (ids: string[]) => void;
  /**
   * Somewhere outside the list an item can be dragged to. With it, a list of one can be lifted too, and a
   * pointer that leaves the panel takes the item out of the list's order: the list closes back up, and
   * letting go there is the host's `drop`, never a reorder.
   */
  dropOut?: DirectoryDropOut;

  /** Controlled menu. Leave undefined and the panel holds it. */
  openMenu?: DirectoryMenu | null;
  onOpenMenuChange?: (menu: DirectoryMenu | null) => void;

  /**
   * Drop the panel's own 36px band. The merged header (Guri, 2026-08-30) hosts the kind switcher
   * and the add on the application bar instead, so the panel is just its list — the host that sets
   * this OWNS re-homing those two controls, because a headerless panel with no switcher anywhere
   * strands three of the four kinds. Row menus are untouched: they anchor to rows, not the band.
   */
  hideHeader?: boolean;
  /**
   * Pinned under the list, inside the panel — the merged header's home for the add ("that plus
   * goes inside the directory panel", Guri, 2026-08-30). A slot rather than a built-in row for
   * the same reason the header's `contextual` is one: what the control is and what menu it opens
   * is the host's business, and the panel only owns where it sits.
   */
  footer?: React.ReactNode;

  theme?: HeaderTheme;
  /**
   * Stories and visual tests only: `'kind'`, `'layout'`, `'add'`, or the id of the row to paint
   * hovered. Reserved names beat ids, so do not name an item `kind`, `layout` or `add`.
   */
  forceHover?: string | null;
  className?: string;
}

/**
 * The Directory: the 252px pane that lists what a deck is made of.
 *
 * One panel for all four kinds, switched from the caret in its own 36px header — not four panels,
 * and not a tab strip. The list underneath is flat: no folders, no groups, no nesting and no
 * dividers anywhere, because a deck's contents are a sequence and the only structure a sequence has
 * is its order.
 */
export function DirectoryPanel({
  kind: kindProp,
  defaultKind = 'artifacts',
  onKindChange,
  artifactLayout: artifactLayoutProp,
  defaultArtifactLayout = 'grid',
  onArtifactLayoutChange,
  renderWorking,
  items,
  sections,
  count,
  selectedId = null,
  onSelect,
  loading = false,
  onAdd,
  onConnectApps,
  connectedApps,
  onRowAction,
  menuRowsFor,
  hideRowMenu = false,
  onRename,
  onReorder,
  dropOut,
  openMenu,
  onOpenMenuChange,
  hideHeader = false,
  footer,
  theme: themeProp,
  forceHover = null,
  className,
}: DirectoryPanelProps) {
  const theme = useV15Theme(themeProp);
  const rootRef = useRef<HTMLDivElement>(null);

  const [internalKind, setInternalKind] = useState<DirectoryView>(defaultKind);
  const kind = kindProp !== undefined ? kindProp : internalKind;
  const setKind = useCallback(
    (next: DirectoryView) => {
      if (kindProp === undefined) setInternalKind(next);
      onKindChange?.(next);
    },
    [kindProp, onKindChange]
  );

  const [internalLayout, setInternalLayout] = useState<ArtifactLayout>(defaultArtifactLayout);
  const artifactLayout = artifactLayoutProp !== undefined ? artifactLayoutProp : internalLayout;
  const setArtifactLayout = useCallback(
    (next: ArtifactLayout) => {
      if (artifactLayoutProp === undefined) setInternalLayout(next);
      onArtifactLayoutChange?.(next);
    },
    [artifactLayoutProp, onArtifactLayoutChange]
  );
  /** Artifacts take the one-track grid as cards; as a list they are rows on `.list`'s own column. */
  const artifactCards = artifactLayout === 'grid';

  /**
   * The DirectoryKind an item renders as.
   *
   * A kind view is itself. The two views that are NOT kinds — `all` and `other` — have no kind of
   * their own to lend, so each item wears the kind of the section it is in: its real one in the
   * blend, and the layout kind `otherSources` assigns in Other sources. Falls back to artifacts
   * only for an id no section claims, which should not happen and must not crash.
   */
  const kindOfItem = useCallback(
    (id: string): DirectoryKind => {
      if (kind !== 'all' && kind !== 'other') return kind;
      return sections?.find((section) => section.items.some((item) => item.id === id))?.kind ?? 'artifacts';
    },
    [kind, sections]
  );

  const [internalMenu, setInternalMenu] = useState<DirectoryMenu | null>(null);
  const menu = openMenu !== undefined ? openMenu : internalMenu;
  const setMenu = useCallback(
    (next: DirectoryMenu | null) => {
      if (openMenu === undefined) setInternalMenu(next);
      onOpenMenuChange?.(next);
    },
    [openMenu, onOpenMenuChange]
  );

  const { rendered, closing } = useMenuPresence(menu);

  /** The one row under the caret, if any. Exactly one, which is why it lives here and not in a row. */
  const [editingId, setEditingId] = useState<string | null>(null);

  // A row that leaves the list — deleted, or switched away from by kind — must not keep the caret.
  useEffect(() => {
    if (editingId && !items.some((item) => item.id === editingId)) setEditingId(null);
  }, [items, editingId]);

  // Reordering while a name is open would drag the field out from under the cursor. A list of one has
  // nowhere to reorder to, but can still be dragged out.
  const sortable =
    (onReorder !== undefined || dropOut !== undefined) &&
    !loading &&
    (items.length > 1 || dropOut !== undefined) &&
    editingId === null;
  const sortableItems = useMemo(() => items.filter((item) => !item.pending), [items]);
  const reorder = useDirectoryReorder({
    items: sortableItems,
    onReorder,
    grid: kind === 'images',
  });

  /** The pointer is off the panel: over whatever the host takes drops on, or nowhere. */
  const offPanel = useCallback((point: { x: number; y: number }) => {
    const rect = rootRef.current?.getBoundingClientRect();
    return !!rect && (point.x < rect.left || point.x > rect.right || point.y < rect.top || point.y > rect.bottom);
  }, []);
  /**
   * Off the panel an item is over no gap at all, so the list closes back up behind it instead of holding a
   * hole open for a reorder nobody is making. Without a `dropOut` the drag keeps dnd-kit's own answer.
   */
  const collision = useCallback<CollisionDetection>(
    (args) => (dropOut && args.pointerCoordinates && offPanel(args.pointerCoordinates) ? [] : closestCenter(args)),
    [dropOut, offPanel],
  );
  /** This drag went to the host's `start`, so its end goes to `end`. */
  const outRef = useRef(false);
  const dragStart = (event: DragStartEvent) => {
    reorder.onDragStart(event);
    if (dropOut && pointerOf({ activatorEvent: event.activatorEvent, delta: { x: 0, y: 0 } })) {
      outRef.current = true;
      dropOut.start(String(event.active.id));
    }
  };
  const finishOut = () => {
    if (!outRef.current) return;
    outRef.current = false;
    dropOut?.end();
  };
  const dragEnd = (event: DragEndEvent) => {
    const point = outRef.current ? pointerOf(event) : null;
    if (dropOut && point && offPanel(point)) dropOut.drop(String(event.active.id), point);
    // Off the panel there is no `over`, so this only puts the lifted copy away.
    reorder.onDragEnd(event);
    finishOut();
  };
  const dragCancel = () => {
    reorder.onDragCancel();
    finishOut();
  };
  // The panel going mid-drag (the view switched away) ends the drag for the host too.
  const dropOutRef = useRef(dropOut);
  dropOutRef.current = dropOut;
  useEffect(
    () => () => {
      if (outRef.current) dropOutRef.current?.end();
      outRef.current = false;
    },
    [],
  );

  /**
   * Where the row menu's top edge lands, relative to the panel. It is the only runtime value in
   * this component, which is exactly what a custom property is for.
   */
  const [rowMenuY, setRowMenuY] = useState(0);

  const menuLayerRef = useRef<HTMLDivElement>(null);
  /** The control the open menu came from, so closing can put focus back where it started. */
  const triggerRef = useRef<HTMLElement | null>(null);

  /**
   * Place the row menu against its row.
   *
   * Measured here, in a layout effect, rather than off the click that opened it: a controlled
   * caller can open this menu without any pointer ever touching the `⋯`, and a menu whose position
   * depends on how it was opened lands in two different places. `useLayoutEffect` runs before
   * paint, so the first frame the eye sees is already in the right place.
   *
   * The menu hangs off the `⋯` ITSELF, not off the row's bottom edge, and is pulled back up when that
   * would put it through the floor — the panel clips, so a menu opened on the last row would otherwise
   * lose the rows below the fold, Delete included.
   *
   * The control and not the row, because the row is not always 46px tall. In the grid layout a row IS a
   * card — around 230px of it — with the `⋯` in its top-right corner, so hanging the menu off the bottom
   * of that dropped it the height of the whole card away from the glyph that opened it. It still grew
   * out of `top right`, which by then was a corner nothing had been clicked in, and the effect was a
   * menu that read as slow and unrelated rather than as the same object opening (Beka, 2026-09-18).
   * Off the control it lands 6px under the `⋯` in both layouts, which is where the preview bar's copy
   * of this menu has always been.
   */
  /**
   * The open row's menu rows — the host's per-item cut, or the default three. Resolved here once so
   * the clamp and the render agree on the menu's height; an item deleted while its menu is open
   * resolves to the default, which is moot because every path that deletes also closes.
   */
  const openRowItem =
    rendered?.menu === 'row' ? items.find((item) => item.id === rendered.rowId) : undefined;
  const openRowMenuRows =
    menuRowsFor && openRowItem ? menuRowsFor(openRowItem, kindOfItem(openRowItem.id)) : undefined;

  /**
   * THE SELECTED ROW STAYS IN VIEW.
   *
   * Selection does not only come from this list. A mention in the chat, an artifact card in a turn,
   * a freshly created item — each of them opens a row from somewhere else on screen, and until now
   * the preview changed while the directory sat exactly where it was. Open the tenth artifact from a
   * sentence and nothing in the list moved, so there was no way to see WHICH of them had opened
   * (Beka, 2026-09-20).
   *
   * Nearest is the whole of the politeness. A row already fully on screen scrolls by zero — so
   * pressing a row in this list never moves it under your own finger — and a row above or below the
   * fold comes just far enough to be seen, rather than jumping to the middle.
   *
   * Only this list scrolls, never the page. `scrollIntoView` moves every scrolling ancestor, so where
   * the panel sits inside a page that scrolls (the landing's live demo), each row that opened dragged
   * the whole page down to reach it.
   *
   * Once per id, tracked rather than keyed on `items`. The row for a selected id may not exist yet:
   * a reload restores a selection before the list has loaded, and a newly minted item is selected on
   * the frame it is created. So this also runs when the items change — but a `selectedId` it has
   * already delivered is left alone, or every blob refetch would drag a reader who had scrolled
   * elsewhere back to a row they are not looking at.
   */
  const scrolledToRef = useRef<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!selectedId) {
      scrolledToRef.current = null;
      return;
    }
    if (scrolledToRef.current === selectedId) return;
    const row = rootRef.current?.querySelector<HTMLElement>(
      `[data-directory-row="${CSS.escape(selectedId)}"]`,
    );
    const list = listRef.current;
    if (!row || !list) return;
    scrolledToRef.current = selectedId;
    const rowBox = row.getBoundingClientRect();
    const listBox = list.getBoundingClientRect();
    const above = rowBox.top - listBox.top;
    const below = rowBox.bottom - listBox.bottom;
    const delta = above < 0 ? above : below > 0 ? Math.min(below, above) : 0;
    if (delta === 0) return;
    // Smooth, except where stillness was asked for — scrolling by hand does not consult the query
    // itself, so a reduced-motion reader would otherwise get the one animation in v1.5 that ignores
    // them.
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    list.scrollBy({ top: delta, behavior: still ? 'auto' : 'smooth' });
  }, [items, selectedId]);

  const openKey = menuKey(rendered);
  useIsomorphicLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || rendered?.menu !== 'row') return;
    const row = root.querySelector<HTMLElement>(
      `[data-directory-row="${CSS.escape(rendered.rowId)}"]`
    );
    if (!row) return;
    // The `⋯` announces itself: it is the only thing in a row carrying `aria-haspopup="menu"`. Falling
    // back to the row keeps a row that somehow has no trigger behaving exactly as it used to.
    const trigger = row.querySelector<HTMLElement>('[aria-haspopup="menu"]') ?? row;
    const anchorBox = trigger.getBoundingClientRect();
    const rootBox = root.getBoundingClientRect();
    const ceiling = rootBox.height - rowMenuHeight(openRowMenuRows?.length ?? 4) - MENU_EDGE_INSET;
    const under = anchorBox.bottom - rootBox.top + MENU_ANCHOR_GAP;
    setRowMenuY(Math.max(MENU_EDGE_INSET, Math.min(under, ceiling)));
    // Keyed on the identity string so this runs once per open, not on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey]);

  const toggleRowMenu = useCallback(
    (id: string) => {
      // Clicking the same `⋯` again closes it, which is what a pressed-looking button should do.
      const open = menu?.menu === 'row' && menu.rowId === id;
      setMenu(open ? null : { menu: 'row', rowId: id });
    },
    [menu, setMenu]
  );

  // Escape closes whatever is open, and an outside pointer does the same — a menu that outlives the
  // click that dismissed it is the single most reported bug shape in this codebase's popovers. The
  // panel itself is exempt, exactly as the header's bar is: every trigger in here toggles on its
  // own click, and closing on the way down would make the second click on a `+` reopen the menu it
  // was meant to shut. Everything that acts — picking a kind, an item, a row action — closes it.
  useEffect(() => {
    if (!menu) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenu(null);
    };
    /*
     * Outside the MENU, not outside the panel.
     *
     * `rootRef` is the whole 252px column, so a press on any row, on the header band, or on the
     * empty space below the list left the menu standing — you had to leave the directory entirely
     * to dismiss it. `menuLayerRef` already exists for the focus work below; this is the same
     * element, asked the question it was always the right element for.
     *
     * The trigger exemption is load-bearing: a `⋯` or the `+` closing here would have its own click
     * re-open the menu a moment later, so the button could never close what it opened. Both wear
     * `aria-haspopup="menu"`, which is the honest test — they are the controls that own popups.
     */
    const onDown = (event: PointerEvent) => {
      const el = event.target instanceof Element ? event.target : null;
      if (menuLayerRef.current?.contains(event.target as Node)) return;
      if (el?.closest('[aria-haspopup="menu"]')) return;
      setMenu(null);
    };
    // Scrolling closes it too, and that one is not politeness. The row menu is placed once, against
    // the row's box, and the list underneath it scrolls on its own — so left open it drifts by
    // exactly the scroll distance: it ends up hanging under a DIFFERENT row while every action in
    // it still targets the original id, and past the pane's bottom edge `overflow: clip` erases it
    // while the `⋯` stays lit and Escape is still required to dismiss what you can no longer see.
    // A menu anchored to a moving row has no correct resting place, so it leaves with the row.
    // Capture, because `scroll` does not bubble; passive, because this never calls preventDefault.
    const onScroll = () => setMenu(null);
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('scroll', onScroll, {
      capture: true,
      passive: true,
    });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [menu, setMenu]);

  /**
   * Focus follows the menu in.
   *
   * The layer is the panel's LAST child — after the header and after every row — so tabbing to the
   * menu you just opened means walking the whole list to reach it, which in a real deck is fifteen
   * or more presses to reach Duplicate. This is the only route to Duplicate / Download / Delete and
   * to all five add actions, so without it those actions are effectively keyboard-unreachable.
   *
   * Keyed on the RENDERED menu rather than the requested one: the surface reaches the DOM a render
   * later than the state that asked for it, and there is nothing to focus before it does.
   */
  useIsomorphicLayoutEffect(() => {
    const layer = menuLayerRef.current;
    if (openKey === null || !layer) return;
    // Read the trigger BEFORE moving off it. A controlled caller can open a menu with focus nowhere
    // near the panel, and there is nothing to hand back to in that case.
    const from = document.activeElement;
    triggerRef.current = from instanceof HTMLElement && !layer.contains(from) ? from : null;
    // `preventScroll`, because the menu is already fully inside the pane and a scroll started by
    // this focus would immediately hit the dismissal above and close the menu we just opened.
    layer.querySelector<HTMLElement>(MENU_ITEM)?.focus({ preventScroll: true });
  }, [openKey]);

  /**
   * And back out on close, to the `⋯` or `+` it came from.
   *
   * Every action unmounts the row the user is standing on, and a focused node removed from the
   * document drops focus to `<body>` — so the next Tab restarts from the top of the page instead of
   * from the control they opened the menu with. Keyed on the REQUESTED menu, not the rendered one,
   * so focus comes back on the click rather than 120ms later when the exit finishes.
   */
  const liveKey = menuKey(menu);
  useEffect(() => {
    if (liveKey !== null) return;
    const back = triggerRef.current;
    triggerRef.current = null;
    if (!back?.isConnected) return;
    // Only take focus back if the menu still holds it, or has just lost it to nothing. A click
    // outside also closes the menu, and whatever that click landed on has the better claim.
    const active = document.activeElement;
    const inMenu = active instanceof Node && menuLayerRef.current?.contains(active);
    if (inMenu || active === null || active === document.body) back.focus();
  }, [liveKey]);

  /**
   * The menu-button keyboard contract, on the layer rather than on `MenuSurface`: arrows are how
   * you move inside a `role="menu"`, and without them the roles announce a menu that answers no
   * menu key. It lives here because the shipped header's menus are a reviewed surface this pass
   * does not own — the Directory's three are the ones being built.
   *
   * Escape is deliberately NOT here; it is on the document above, so a menu opened by a controlled
   * caller with focus elsewhere still closes.
   */
  useEffect(() => {
    const layer = menuLayerRef.current;
    if (openKey === null || !layer) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        // A menu is a mode and Tab leaves it. Focus goes back to the trigger (the effect above),
        // never on into a list that is about to unmount underneath it.
        event.preventDefault();
        setMenu(null);
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
      const end = event.key === 'Home' ? 'first' : event.key === 'End' ? 'last' : null;
      if (step === 0 && end === null) return;
      const items = Array.from(layer.querySelectorAll<HTMLElement>(MENU_ITEM));
      if (items.length === 0) return;
      // Only now: an unhandled key must keep its default, and ArrowDown scrolls a page.
      event.preventDefault();
      if (end !== null) {
        items[end === 'first' ? 0 : items.length - 1].focus();
        return;
      }
      const at = items.indexOf(document.activeElement as HTMLElement);
      // Wraps. Three rows and four kinds are short enough that stopping at the end reads as jammed.
      items[(at + step + items.length) % items.length].focus();
    };
    // A native listener on the layer rather than an `onKeyDown` prop: the layer is a positioning
    // wrapper with no role of its own, and hanging a handler on it as JSX makes it look like the
    // interactive element it is not. The keys belong to the `role="menu"` inside it, and this is
    // the closest a wrapper can get to saying so.
    layer.addEventListener('keydown', onKey);
    return () => layer.removeEventListener('keydown', onKey);
  }, [openKey, setMenu]);

  const empty = !loading && items.length === 0;

  /**
   * The list itself, lifted into a variable because it is rendered from two places: bare, and
   * inside the `DndContext` that makes it sortable. Wrapping in place would mean writing it twice.
   */
  const renderEntry = (item: DirectoryItem) => {
    const itemKind = kindOfItem(item.id);
    // Not made yet: drawn in its state, wired to nothing, and never one of the sortable items.
    if (item.pending) {
      const inert = { item, kind: itemKind, theme };
      if (itemKind === 'artifacts' && artifactCards) {
        return <ArtifactCard key={item.id} {...inert} working={renderWorking?.(item)} />;
      }
      return itemKind === 'images' ? <ImageCard key={item.id} {...inert} /> : <DirectoryRow key={item.id} {...inert} />;
    }
    const shared = {
      item,
      kind: itemKind,
      selected: item.id === selectedId,
      theme,
      forceHover: forceHover === item.id,
      onClick: () => {
        setMenu(null);
        onSelect?.(item.id);
      },
    };
    // Every kind offers the `⋯` once the host supplies per-kind rows; without that prop
    // images keep their menu-less past. Only ever on a ready item — `DirectoryRow` and
    // `ImageCard` both drop it for anything in flight, and handing them the callback
    // anyway keeps that decision in one place.
    //
    // Other sources never offer it. Its rows are things living in somebody else's app: there is
    // nothing here to rename, duplicate, download or delete, and `KIND_ROW_ACTIONS` has no entry
    // that would be true of one. Header rule 5 — a row is only ever drawn wired — so the whole
    // menu goes rather than a menu of rows that all do nothing.
    const rowOnly =
      hideRowMenu || kind === 'other' || (itemKind === 'images' && !menuRowsFor)
        ? {}
        : {
            menuOpen: menu?.menu === 'row' && menu.rowId === item.id,
            onMoreClick: () => toggleRowMenu(item.id),
            editing: editingId === item.id,
            onEditingChange: (next: boolean) => setEditingId(next ? item.id : null),
            onRename: onRename ? (value: string) => onRename(item.id, value) : undefined,
          };

    // The unsortable branch is the same components with no `useSortable` above them,
    // not a disabled sortable one: a list of one item, or one still loading, should not
    // be paying for sensors and measuring passes it can never use.
    if (!sortable) {
      if (itemKind === 'artifacts' && artifactCards) {
        return <ArtifactCard key={item.id} {...shared} {...rowOnly} working={renderWorking?.(item)} />;
      }
      return itemKind === 'images' ? (
        <ImageCard key={item.id} {...shared} {...rowOnly} />
      ) : (
        <DirectoryRow key={item.id} {...shared} {...rowOnly} />
      );
    }
    return (
      <SortableDirectoryItem
        key={item.id}
        {...shared}
        {...rowOnly}
        artifactLayout={artifactLayout}
        working={itemKind === 'artifacts' ? renderWorking?.(item) : undefined}
      />
    );
  };

  const list = (
    <div
      ref={listRef}
      // Both card kinds take the grid; artifacts narrow it to one track. Rows stay on `.list`'s
      // own flex column, which has a different gutter and a different gap.
      className={cn(
        styles.list,
        (kind === 'images' || (kind === 'artifacts' && artifactCards)) && styles.grid,
        kind === 'artifacts' && artifactCards && styles.gridOne,
      )}
      role="group"
      aria-label={VIEW_LABEL[kind]}
    >
      {loading ? (
        Array.from({ length: SKELETON_ROWS }, (_, i) => <SkeletonRow key={i} theme={theme} />)
      ) : sections ? (
        /* The blend: each kind's items under its own quiet label. An empty kind contributes
           nothing, not a heading over nothing. Images keep their grid inside their group. */
        sections
          .filter((section) => section.items.length > 0)
          .map((section) => (
            <div key={section.id ?? section.kind} className={styles.section}>
              <div className={styles.sectionLabel} aria-hidden>
                {section.label}
              </div>
              <div
                className={cn(
                  (section.kind === 'images' || (section.kind === 'artifacts' && artifactCards)) && styles.grid,
                  section.kind === 'artifacts' && artifactCards && styles.gridOne,
                )}
              >
                {section.items.map(renderEntry)}
              </div>
            </div>
          ))
      ) : (
        items.map(renderEntry)
      )}
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={cn(themes[theme], styles.panel, hideHeader && styles.bare, className)}
    >
      {hideHeader ? null : (
        <DirectoryHeader
          kind={kind}
          count={loading ? undefined : count ?? items.length}
          kindMenuOpen={menu?.menu === 'kind'}
          onKindClick={() => setMenu(menu?.menu === 'kind' ? null : { menu: 'kind' })}
          addMenuOpen={menu?.menu === 'add'}
          onAddClick={() => setMenu(menu?.menu === 'add' ? null : { menu: 'add' })}
          // Nothing the add menu makes lands in Other sources — you connect an app, you do not
          // create one — so that view drops the `+` rather than wearing a label it cannot keep.
          // The route to the integrations screen lives on the empty state, which is where somebody
          // with nothing connected is actually standing.
          hideAdd={kind === 'other'}
          layout={artifactLayout}
          onLayoutClick={
            kind === 'artifacts' ? () => setArtifactLayout(artifactCards ? 'list' : 'grid') : undefined
          }
          theme={theme}
          forceHover={forceHover}
        />
      )}

      {/*
        * An empty KIND draws nothing here (Beka, 2026-09-05).
        *
        * The per-kind boards — "No documents / Documents hold the thinking behind the deck / New
        * document" — moved to the preview, which now says the same thing per kind and has the room
        * to say it properly. Two boards for one absence, side by side, made the deck look twice as
        * empty as it was, and the 252px column had to shorten every sentence to fit.
        *
        * `other` keeps its own, and that is not an exception being smuggled in: it is not a kind and
        * its absence is a different fact — no APPS connected, not no items — and the route out of it
        * is `/integrations`, which the preview's boards have no way to offer.
        */}
      {empty && kind === 'other' ? (
        <OtherSourcesEmpty connected={connectedApps} onConnect={onConnectApps} theme={theme} />
      ) : empty ? null : sortable ? (
        <DndContext
          sensors={reorder.sensors}
          // Centre-to-centre. The default (`rectIntersection`) needs the dragged box to physically
          // overlap a target, which for a 46px row inside a 6px-padded column means the gap only
          // opens once you have dragged most of a row past it. Closest-centre opens it at the
          // halfway point, which is where the eye already expects it.
          collisionDetection={collision}
          modifiers={reorder.modifiers}
          // Rows are not all the same height — a generating one is 56px against everyone else's 46
          // — so a cached measuring pass would open a gap the wrong size the moment a drag crossed
          // one. `Always` re-measures on every move, which is the cost of letting in-flight items
          // be dragged like any other.
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          accessibility={{ announcements: reorder.announcements }}
          onDragStart={(event) => {
            // A row menu is anchored to a row's measured position. Lifting that row would leave the
            // menu behind, pointing at a gap.
            setMenu(null);
            dragStart(event);
          }}
          onDragEnd={dragEnd}
          onDragCancel={dragCancel}
        >
          <SortableContext
            items={reorder.ids}
            strategy={kind === 'images' ? rectSortingStrategy : verticalListSortingStrategy}
          >
            {list}
          </SortableContext>

          {/* The lifted copy, drawn `position: fixed` above everything — which is the only way it
              can leave the list's `overflow-y: auto` without being clipped by it. It is a sibling of
              the list rather than a child for the same reason. */}
          <DragOverlay dropAnimation={DROP_ANIMATION}>
            {reorder.activeItem ? (
              // Whatever the list draws, the overlay draws — an artifact lifting as a row while the
              // hole it left is a card is the drag admitting it is two components.
              kindOfItem(reorder.activeItem.id) === 'artifacts' && artifactCards ? (
                <ArtifactCard
                  item={reorder.activeItem}
                  selected={reorder.activeItem.id === selectedId}
                  theme={theme}
                  overlay
                />
              ) : kind === 'images' ? (
                <ImageCard
                  item={reorder.activeItem}
                  selected={reorder.activeItem.id === selectedId}
                  theme={theme}
                  overlay
                />
              ) : (
                <DirectoryRow
                  item={reorder.activeItem}
                  kind={kindOfItem(reorder.activeItem.id)}
                  selected={reorder.activeItem.id === selectedId}
                  theme={theme}
                  overlay
                />
              )
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        list
      )}

      {/* After the list, so it pins to the panel's bottom edge — the list is the pane's one
          flexing child. Drawn under the empty state too: an empty kind's board offers its own
          CTA, but the footer is a standing fixture, not a state. */}
      {footer ? <div className={styles.footer}>{footer}</div> : null}

      {rendered ? (
        <div
          ref={menuLayerRef}
          className={cn(
            styles.menuLayer,
            rendered.menu === 'kind' ? styles.anchorLeft : styles.anchorRight,
            rendered.menu === 'row' && styles.anchorRow
          )}
          data-closing={closing}
          style={
            rendered.menu === 'row'
              ? ({
                  '--hv15-dir-menu-y': `${rowMenuY}px`,
                } as React.CSSProperties)
              : undefined
          }
        >
          {rendered.menu === 'kind' ? (
            <KindMenu
              kind={kind}
              theme={theme}
              className={styles.menuFit}
              onPick={(next) => {
                setMenu(null);
                setKind(next);
              }}
            />
          ) : rendered.menu === 'add' ? (
            <DirectoryAddMenu
              theme={theme}
              className={styles.menuFit}
              onAdd={(item) => {
                setMenu(null);
                // "Import from Figma" is not a create, so it never reaches the host's create path
                // — where it was a documented dead end that returned null and left the row doing
                // nothing at all. It is a NAVIGATION: it goes to the view that holds material from
                // connected apps, and that view then tells the truth about Figma on its own. When
                // Figma is connected its rows are there; when it is not, the empty state says so
                // and offers the way to connect. Deciding here which of those two the user gets
                // would mean this menu guessing at a connection state it cannot read.
                if (item === 'figma') {
                  setKind('other');
                  return;
                }
                onAdd?.(item);
              }}
            />
          ) : (
            <RowMenu
              rows={openRowMenuRows}
              kind={openRowItem ? kindOfItem(openRowItem.id) : undefined}
              theme={theme}
              className={styles.menuFit}
              renameable={onRename !== undefined}
              onAction={(action) => {
                const id = rendered.rowId;
                // Rename is the menu's route into the same field the name's double-click opens —
                // there is no rename dialog, and a caller asked to "handle rename" would have
                // nothing to handle it with. Exactly how the deck menu's own Rename behaves.
                if (action === 'rename') {
                  setMenu(null);
                  setEditingId(id);
                  return;
                }
                // A result-feedback action holds the menu open to wear its result ("Copied");
                // RowMenu calls onSettled when the beat is over and THAT closes it.
                if (RESULT_FEEDBACK[action] !== undefined) {
                  onRowAction?.(action, id);
                  return;
                }
                setMenu(null);
                onRowAction?.(action, id);
              }}
              onSettled={() => setMenu(null)}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}
